import type { OrchestratorSchemas } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { createQuery, type SkippableArgs } from '../query.ts'

/**
 * 브로커 계정 · 모듈 브로커 접속 조회 — `IFC-RO-69` · `IFC-RO-80` · `IFC-RO-94` · `IFC-RO-78` GET.
 *
 * 실물(`ctcells-core/app/views/broker_accounts.py` · core@d50b8cf4 · dev2 2026-10-12 실측)
 *
 * | 경로 | 역할 | 응답 | dev2 실측 |
 * | --- | --- | --- | --- |
 * | `GET /broker/accounts` | **admin 한정**(`ROLES`) | `BrokerAccount[]` **맨 배열**(쪽 나눔 없음) | 🔴 503 「브로커 제어 unconfigured」 — API 컨테이너에 브로커 관리 자격(`MQTT_ADMIN_*`)이 없다 |
 * | `GET /broker/core` | admin | `BrokerCoreStatus` — 지금 코어 L2 계정 이름(비밀번호 값 없음) | 200 `{account:'l2', source:'env', …}` |
 * | `GET /broker/accounts/{name}/history` | admin | `BrokerAccountHistoryEntry[]` 맨 배열 · 최근 먼저 · `limit`/`offset` | 200 `[]` |
 * | `GET /modules/{id}/broker` | admin | `ModuleBrokerConnection` — **모듈에 HTTP 로 물어 중계**한다(비밀번호는 «있나»만) | `centrifuge-1` 200 · 못 닿는 모듈 503 · 없는 모듈 404 |
 *
 * 🔴 **admin 이 아니면 부르지 않는다**(`skip`) — 403 이 날 요청으로 「못 봤다」를 만들지 않는다.
 * 🔴 주기 폴링 없다 — 스냅샷 1회 + 쓰기 뒤 무효화 + 구독 `broker_change`(`stream/bridge.ts`).
 * ⚠ 모듈 접속은 **`keys.broker` 아래에 둔다**(`keys.modules` 아님) — 모듈 상태 프레임이 `modules.all()` 을 무효화할 때마다
 *   모듈에 HTTP 를 다시 쏘지 않게. 다시 받는 것은 브로커 교체 행이 움직일 때(`broker_change`)와 그 쓰기 뒤뿐이다.
 */
export type BrokerAccount = OrchestratorSchemas['schemas']['BrokerAccount']
export type BrokerAccountChange = OrchestratorSchemas['schemas']['BrokerAccountChange']
export type BrokerAccountHistoryEntry = OrchestratorSchemas['schemas']['BrokerAccountHistoryEntry']
export type BrokerCoreStatus = OrchestratorSchemas['schemas']['BrokerCoreStatus']
export type ModuleBrokerConnection = OrchestratorSchemas['schemas']['ModuleBrokerConnection']

export const useBrokerAccounts = createQuery<SkippableArgs | void, BrokerAccount[]>({
  key: () => keys.broker.accounts(),
  fetch: () =>
    orchestrator.request<BrokerAccount[]>({
      method: 'GET',
      path: '/broker/accounts',
      retryOn401: true,
    }),
})

/** 코어 L2 계정 이름 — 「코어 자기 비밀번호」만 서버가 받는 까닭에 그 한 줄을 가르는 데 쓴다 */
export const useBrokerCore = createQuery<SkippableArgs | void, BrokerCoreStatus>({
  key: () => keys.broker.core(),
  fetch: () =>
    orchestrator.request<BrokerCoreStatus>({
      method: 'GET',
      path: '/broker/core',
      retryOn401: true,
    }),
})

export const useBrokerAccountHistory = createQuery<
  SkippableArgs & { name: string | undefined },
  BrokerAccountHistoryEntry[]
>({
  key: ({ name }) => keys.broker.history(name ?? ''),
  fetch: ({ name }) =>
    orchestrator.request<BrokerAccountHistoryEntry[]>({
      method: 'GET',
      path: `/broker/accounts/${encodeURIComponent(String(name))}/history`,
      retryOn401: true,
    }),
  enabled: ({ name }) => name !== undefined && name !== '',
})

export const useModuleBrokerConnection = createQuery<
  SkippableArgs & { moduleId: string | undefined },
  ModuleBrokerConnection
>({
  key: ({ moduleId }) => keys.broker.moduleConnection(moduleId ?? ''),
  fetch: ({ moduleId }) =>
    orchestrator.request<ModuleBrokerConnection>({
      method: 'GET',
      path: `/modules/${encodeURIComponent(String(moduleId))}/broker`,
      retryOn401: true,
    }),
  enabled: ({ moduleId }) => moduleId !== undefined && moduleId !== '',
})
